import * as React from "react";
import classNames from "classnames";
import { MovePlayerLeft, MovePlayerRight } from "./MovePlayerButton";
import { WebsocketContext } from "./WebsocketProvider";
import { useTranslation } from "./i18n";
/*
 * Inline rename control shown for a seated bot in the lobby. A pencil affordance
 * swaps the bot's name for a small text input that dispatches a RenameBot action
 * over the same websocket `send` mechanism as the other lobby controls:
 *   { Action: { RenameBot: { player: playerId, name } } }
 * Only bots are renamable; humans never get this control.
 */
var BotRename = function (_a) {
    var player = _a.player;
    var send = React.useContext(WebsocketContext).send;
    var t = useTranslation().t;
    var _b = React.useState(false), editing = _b[0], setEditing = _b[1];
    var _c = React.useState(player.name), draft = _c[0], setDraft = _c[1];
    var inputRef = React.useRef(null);
    var open = function () {
        setDraft(player.name);
        setEditing(true);
    };
    var cancel = function () {
        setEditing(false);
    };
    var submit = function () {
        var trimmed = draft.trim();
        // No-op on empty or unchanged names; the server also validates.
        if (trimmed.length > 0 && trimmed !== player.name) {
            send({ Action: { RenameBot: { player: player.id, name: trimmed } } });
        }
        setEditing(false);
    };
    React.useEffect(function () {
        var _a, _b;
        if (editing) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.select();
        }
    }, [editing]);
    if (!editing) {
        return (React.createElement("button", { type: "button", className: "sj-btn sj-btn-ghost !min-h-[28px] !px-2 !text-xs !text-[var(--text-primary)]", onClick: open, title: t("ai.rename"), "aria-label": t("ai.rename") }, "\u270F\uFE0F"));
    }
    return (React.createElement("span", { className: "flex items-center gap-1" },
        React.createElement("input", { ref: inputRef, type: "text", className: "sj-input !min-h-[28px] !w-28 !py-0.5 !text-xs", value: draft, maxLength: 32, "aria-label": t("ai.renameLabel"), onChange: function (e) { return setDraft(e.target.value); }, onKeyDown: function (e) {
                if (e.key === "Enter") {
                    submit();
                }
                else if (e.key === "Escape") {
                    cancel();
                }
            } }),
        React.createElement("button", { type: "button", className: "sj-btn sj-btn-primary !min-h-[28px] !px-2 !text-xs", onClick: submit, title: t("ai.renameSave"), "aria-label": t("ai.renameSave") }, "\u2713"),
        React.createElement("button", { type: "button", className: "sj-btn sj-btn-ghost !min-h-[28px] !px-2 !text-xs !text-[var(--text-primary)]", onClick: cancel, title: t("ai.renameCancel"), "aria-label": t("ai.renameCancel") }, "\u2715")));
};
var Players = function (props) {
    var players = props.players, observers = props.observers, bots = props.bots, landlord = props.landlord, landlords_team = props.landlords_team, gameMode = props.gameMode, movable = props.movable, next = props.next, name = props.name;
    var send = React.useContext(WebsocketContext).send;
    var t = useTranslation().t;
    var botById = React.useMemo(function () {
        var m = {};
        (bots !== null && bots !== void 0 ? bots : []).forEach(function (b) {
            m[b.player_id] = b;
        });
        return m;
    }, [bots]);
    // In fixed-partnership Tractor, partnerships are decided purely by seat
    // (the engine teams players whose seat index shares the landlord's parity —
    // i.e. partners sit across, +2). With no landlord chosen yet we can still
    // show the local player which seats are on their team vs the opponents:
    // everyone at the same seat-parity as "you" is a teammate. Only meaningful
    // once all 4 seats are filled. FindingFriends has no fixed pre-game teams.
    var isTractor = gameMode === "Tractor";
    var selfSeatIndex = players.findIndex(function (p) { return p.name === name; });
    var showLobbyTeams = isTractor && players.length === 4 && selfSeatIndex >= 0;
    var teamRoleOfSeat = function (seatIndex) {
        return seatIndex % 2 === selfSeatIndex % 2 ? "self-team" : "opponent";
    };
    var makeDescriptor = function (p) {
        if (p.metalevel <= 1) {
            return ["".concat(p.name, " (").concat(t("common.rank"), " ").concat(p.level, ")")];
        }
        else {
            return [
                "".concat(p.name, " (").concat(t("common.rank"), " ").concat(p.level),
                React.createElement("sup", { key: "meta-".concat(p.id) }, p.metalevel),
                ")",
            ];
        }
    };
    // A small explanatory line under the lobby roster so players understand how
    // teams form before the game starts (and we never fake teams in FF).
    var teamNote = gameMode === undefined
        ? null
        : isTractor
            ? showLobbyTeams
                ? t("lobby.team.fixedHint")
                : null
            : t("lobby.team.decidedInPlay");
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "players flex flex-wrap gap-2" },
            players.map(function (player, seatIndex) {
                var isLandlord = player.id === landlord || (landlords_team === null || landlords_team === void 0 ? void 0 : landlords_team.includes(player.id));
                var isNext = player.id === next;
                var bot = botById[player.id];
                // Color every seat by its fixed team (seat-index parity). A
                // continuation game can pre-set a landlord, but the fixed partnerships
                // don't change, so the team colors must stay consistent — the declarer
                // is marked by the "Declarer" text, not by the landlord highlight.
                var teamRole = showLobbyTeams ? teamRoleOfSeat(seatIndex) : null;
                var className = classNames("player relative flex min-w-[8.5rem] flex-col rounded-xl border px-3 py-2 text-sm transition", {
                    "border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_14%,transparent)]": isLandlord && !showLobbyTeams,
                    "sj-lobby-team-self": teamRole === "self-team",
                    "sj-lobby-team-opponent": teamRole === "opponent",
                    "border-[var(--border-subtle)] bg-[var(--surface-panel-soft)]": !isLandlord && teamRole === null,
                    "ring-2 ring-[var(--accent)]": isNext,
                    movable: movable,
                });
                var descriptor = makeDescriptor(player);
                if (player.id === landlord) {
                    descriptor.push(" ".concat(t("term.banker")));
                }
                if (player.name === name) {
                    descriptor.push(" (".concat(t("common.you"), ")"));
                }
                return (React.createElement("div", { key: player.id, className: className },
                    teamRole !== null && (React.createElement("span", { className: classNames("sj-lobby-team-tag", {
                            "sj-lobby-team-tag-self": teamRole === "self-team",
                            "sj-lobby-team-tag-opponent": teamRole === "opponent",
                        }) }, teamRole === "self-team"
                        ? t("lobby.team.yourTeam")
                        : t("lobby.team.opponents"))),
                    React.createElement("span", { className: "flex items-center gap-1 font-semibold" },
                        bot !== undefined && (React.createElement("span", { "aria-hidden": "true", title: "".concat(t("ai.botLabel"), " \u00B7 ").concat(t("ai.difficulty.".concat(bot.difficulty))) }, "\uD83E\uDD16")),
                        descriptor),
                    bot !== undefined && (React.createElement("span", { className: "sj-chip sj-chip-accent mt-1 w-fit" }, t("ai.difficulty.".concat(bot.difficulty)))),
                    movable && (React.createElement("span", { className: "mt-2 flex items-center justify-center gap-1" },
                        React.createElement(MovePlayerLeft, { players: players, player: player }),
                        bot !== undefined ? (React.createElement(React.Fragment, null,
                            React.createElement(BotRename, { player: player }),
                            React.createElement("button", { type: "button", className: "sj-btn sj-btn-ghost !min-h-[28px] !px-2 !text-xs !text-[var(--text-primary)]", onClick: function () {
                                    return send({ Action: { RemoveAIPlayer: player.id } });
                                }, title: t("ai.remove") }, t("ai.remove")))) : (React.createElement("span", { role: "button", tabIndex: 0, style: { cursor: "pointer" }, onClick: function () {
                                return send({ Action: { MakeObserver: player.id } });
                            }, onKeyDown: function (e) {
                                if (e.key === "Enter" || e.key === " ") {
                                    send({ Action: { MakeObserver: player.id } });
                                }
                            }, title: "Make observer" }, "\u2714\uFE0F")),
                        React.createElement(MovePlayerRight, { players: players, player: player })))));
            }),
            observers.map(function (player) {
                var descriptor = makeDescriptor(player);
                if (player.name === name) {
                    descriptor.push(" (".concat(t("common.you"), ")"));
                }
                return (React.createElement("div", { key: player.id, className: "player observer flex min-w-[8.5rem] flex-col rounded-xl border border-dashed border-[var(--border-strong)] px-3 py-2 text-sm text-[var(--text-secondary)]" },
                    React.createElement("span", { style: { textDecoration: "line-through" } }, descriptor),
                    movable && (React.createElement("span", { className: "mt-2 flex items-center justify-center" },
                        React.createElement("span", { role: "button", tabIndex: 0, style: { cursor: "pointer" }, onClick: function () { return send({ Action: { MakePlayer: player.id } }); }, onKeyDown: function (e) {
                                if (e.key === "Enter" || e.key === " ") {
                                    send({ Action: { MakePlayer: player.id } });
                                }
                            }, title: "Make player" }, "\uD83D\uDCA4")))));
            })),
        teamNote !== null && (React.createElement("p", { className: "mt-2 text-xs text-[var(--text-secondary)]" }, teamNote))));
};
export default Players;
